import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AuthImage } from './AuthImage'
import { Button, Modal } from './ui'
import '../styles/photo-viewer.css'

export interface ViewerPhoto {
  id: string
  src: string
  caption: string
  local?: boolean
}

/** Просмотр использует тот же защищённый оригинал и офлайн-кэш, что и миниатюры. */
export function PhotoViewer({ photos, initialId, onClose }: { photos: ViewerPhoto[]; initialId: string; onClose(): void }) {
  const [index, setIndex] = useState(() => Math.max(0, photos.findIndex((photo) => photo.id === initialId)))
  const [zoomed, setZoomed] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const photo = photos[index]

  function move(direction: number) {
    setIndex((current) => (current + direction + photos.length) % photos.length)
    setZoomed(false)
  }

  useEffect(() => {
    if (photos.length < 2) return
    function onKey(event: KeyboardEvent) {
      // В увеличенном снимке стрелки прокручивают изображение.
      if (zoomed || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault()
      setIndex((current) => (current + (event.key === 'ArrowRight' ? 1 : -1) + photos.length) % photos.length)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [photos.length, zoomed])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    viewport.scrollLeft = zoomed ? (viewport.scrollWidth - viewport.clientWidth) / 2 : 0
    viewport.scrollTop = zoomed ? (viewport.scrollHeight - viewport.clientHeight) / 2 : 0
  }, [index, zoomed])

  if (!photo) return null
  return (
    <Modal open onClose={onClose} title={`Фото ${index + 1} из ${photos.length}`} size="large" className="photo-viewer-modal">
      <div className="photo-viewer">
        <div className={`photo-viewer__viewport${zoomed ? ' photo-viewer__viewport--zoomed' : ''}`} ref={viewportRef} tabIndex={0} aria-label={zoomed ? 'Увеличенное фото. Прокручивайте, чтобы рассмотреть детали' : 'Просмотр фото'}>
          <div className="photo-viewer__canvas">
            {photo.local
              ? <img src={photo.src} alt={photo.caption} draggable={false} />
              : <AuthImage src={photo.src} alt={photo.caption} draggable={false} />}
          </div>
        </div>
        <div className="photo-viewer__controls">
          {photos.length > 1 && <Button type="button" variant="secondary" onClick={() => move(-1)} aria-label="Предыдущее фото"><ChevronLeft size={20} aria-hidden="true" /></Button>}
          <Button type="button" variant="secondary" className="photo-viewer__zoom" aria-label={zoomed ? 'Уменьшить фото' : 'Увеличить фото'} aria-pressed={zoomed} onClick={() => setZoomed((value) => !value)}>
            {zoomed ? <ZoomOut size={20} aria-hidden="true" /> : <ZoomIn size={20} aria-hidden="true" />}{zoomed ? 'Уменьшить' : 'Увеличить'}
          </Button>
          {photos.length > 1 && <Button type="button" variant="secondary" onClick={() => move(1)} aria-label="Следующее фото"><ChevronRight size={20} aria-hidden="true" /></Button>}
        </div>
        <p className="photo-viewer__caption" aria-live="polite">{photo.caption}</p>
      </div>
    </Modal>
  )
}
